// Core utilities adapted for React frontend
// Shared utility functions used across the application

/**
 * Async utility functions
 */
export const asyncUtils = {
  /**
   * Sleep/delay utility
   * @param {number} ms - Milliseconds to delay
   * @returns {Promise} Promise that resolves after delay
   */
  sleep: (ms) => new Promise(resolve => setTimeout(resolve, ms)),

  /**
   * Retry utility for async operations
   * @param {Function} operation - Async function to retry
   * @param {number} maxAttempts - Maximum retry attempts
   * @param {number} delay - Delay between retries in ms
   * @returns {Promise} Result of successful operation
   */
  retry: async (operation, maxAttempts = 3, delay = 1000) => {
    let lastError;

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        return await operation();
      } catch (error) {
        lastError = error;

        if (window.logger) {
          window.logger.warn({
            tags: "utils|async|retry",
            color1: "yellow",
            includeSource: true
          }, `Attempt ${attempt}/${maxAttempts} failed:`, error.message);
        }

        if (attempt < maxAttempts) {
          await asyncUtils.sleep(delay);
        }
      }
    }

    throw lastError;
  },

  /**
   * Timeout wrapper for async operations
   * @param {Promise} promise - Promise to wrap with timeout
   * @param {number} timeoutMs - Timeout in milliseconds
   * @param {string} timeoutMessage - Custom timeout message
   * @returns {Promise} Promise that rejects on timeout
   */
  withTimeout: (promise, timeoutMs, timeoutMessage = 'Operation timed out') => {
    return Promise.race([
      promise,
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error(timeoutMessage)), timeoutMs)
      )
    ]);
  }
};

/**
 * DOM utility functions
 */
export const domUtils = {
  /**
   * Safe query selector with error handling
   * @param {string} selector - CSS selector
   * @param {Element} context - Context element (defaults to document)
   * @returns {Element|null} Found element or null
   */
  querySelector: (selector, context = document) => {
    try {
      return context.querySelector(selector);
    } catch (error) {
      if (window.logger) {
        window.logger.error({
          tags: "utils|dom|query",
          color1: "red",
          color2: "orange",
          includeSource: true
        }, `Invalid selector: ${selector}`, error);
      }
      return null;
    }
  },

  /**
   * Safe query selector all
   * @param {string} selector - CSS selector
   * @param {Element} context - Context element (defaults to document)
   * @returns {NodeList} Found elements
   */
  querySelectorAll: (selector, context = document) => {
    try {
      return context.querySelectorAll(selector);
    } catch (error) {
      if (window.logger) {
        window.logger.error({
          tags: "utils|dom|query",
          color1: "red",
          color2: "orange",
          includeSource: true
        }, `Invalid selector: ${selector}`, error);
      }
      return [];
    }
  },

  /**
   * Check if element is visible in viewport
   * @param {Element} element - Element to check
   * @returns {boolean} Whether element is visible
   */
  isVisible: (element) => {
    if (!element) return false;

    const rect = element.getBoundingClientRect();
    return (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
      rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    );
  },

  /**
   * Get element dimensions safely
   * @param {Element} element - Element to measure
   * @returns {Object} Dimensions object or empty object on error
   */
  getDimensions: (element) => {
    try {
      const rect = element.getBoundingClientRect();
      return {
        width: rect.width,
        height: rect.height,
        top: rect.top,
        left: rect.left,
        bottom: rect.bottom,
        right: rect.right
      };
    } catch (error) {
      if (window.logger) {
        window.logger.warn({
          tags: "utils|dom|dimensions",
          color1: "yellow",
          includeSource: true
        }, "Failed to get element dimensions:", error);
      }
      return {};
    }
  }
};

/**
 * Data validation and sanitization utilities
 */
export const dataUtils = {
  /**
   * Safe JSON parse with fallback
   * @param {string} jsonString - JSON string to parse
   * @param {*} fallback - Fallback value if parsing fails
   * @returns {*} Parsed object or fallback
   */
  safeJsonParse: (jsonString, fallback = null) => {
    try {
      return JSON.parse(jsonString);
    } catch (error) {
      if (window.logger) {
        window.logger.warn({
          tags: "utils|data|json",
          color1: "yellow",
          includeSource: true
        }, "Failed to parse JSON:", jsonString, error);
      }
      return fallback;
    }
  },

  /**
   * Safe JSON stringify with error handling
   * @param {*} data - Data to stringify
   * @param {*} fallback - Fallback value if stringifying fails
   * @returns {string|null} JSON string or fallback
   */
  safeJsonStringify: (data, fallback = null) => {
    try {
      return JSON.stringify(data);
    } catch (error) {
      if (window.logger) {
        window.logger.error({
          tags: "utils|data|json",
          color1: "red",
          color2: "orange",
          includeSource: true
        }, "Failed to stringify JSON:", data, error);
      }
      return fallback;
    }
  },

  /**
   * Deep clone utility
   * @param {*} obj - Object to clone
   * @returns {*} Cloned object or original if cloning fails
   */
  deepClone: (obj) => {
    try {
      return JSON.parse(JSON.stringify(obj));
    } catch (error) {
      if (window.logger) {
        window.logger.warn({
          tags: "utils|data|clone",
          color1: "yellow",
          includeSource: true
        }, "Failed to deep clone:", obj, error);
      }
      return obj;
    }
  },

  /**
   * Type checking utilities
   */
  is: {
    string: (value) => typeof value === 'string',
    number: (value) => typeof value === 'number' && !isNaN(value),
    boolean: (value) => typeof value === 'boolean',
    array: (value) => Array.isArray(value),
    object: (value) => value !== null && typeof value === 'object' && !Array.isArray(value),
    function: (value) => typeof value === 'function',
    defined: (value) => value !== undefined && value !== null,
    empty: (value) => {
      if (!dataUtils.is.defined(value)) return true;
      if (dataUtils.is.string(value) || dataUtils.is.array(value)) return value.length === 0;
      if (dataUtils.is.object(value)) return Object.keys(value).length === 0;
      return false;
    }
  }
};

/**
 * Event handling utilities
 */
export const eventUtils = {
  /**
   * Debounce function calls
   * @param {Function} func - Function to debounce
   * @param {number} wait - Wait time in milliseconds
   * @returns {Function} Debounced function
   */
  debounce: (func, wait) => {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  },

  /**
   * Throttle function calls
   * @param {Function} func - Function to throttle
   * @param {number} limit - Time limit in milliseconds
   * @returns {Function} Throttled function
   */
  throttle: (func, limit) => {
    let inThrottle;
    return function executedFunction(...args) {
      if (!inThrottle) {
        func.apply(this, args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    };
  }
};

/**
 * Browser and environment utilities
 */
export const envUtils = {
  /**
   * Check if running in development
   * @returns {boolean} Whether in development mode
   */
  isDevelopment: () => {
    return process.env.NODE_ENV === 'development' ||
           window.location.hostname === 'localhost' ||
           window.location.hostname === '127.0.0.1';
  },

  /**
   * Check if running in production
   * @returns {boolean} Whether in production mode
   */
  isProduction: () => {
    return process.env.NODE_ENV === 'production';
  },

  /**
   * Get current environment name
   * @returns {string} Environment name
   */
  getEnvironment: () => {
    return process.env.NODE_ENV || 'development';
  },

  /**
   * Browser detection utilities
   */
  browser: {
    isChrome: () => navigator.userAgent.includes('Chrome'),
    isFirefox: () => navigator.userAgent.includes('Firefox'),
    isSafari: () => navigator.userAgent.includes('Safari') && !navigator.userAgent.includes('Chrome'),
    isEdge: () => navigator.userAgent.includes('Edg'),
    isMobile: () => /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
  },

  /**
   * Device and environment detection utilities
   */
  device: {
    /**
     * Check if running in Revolt Desktop Electron app.
     * Relies on the explicit flag set by Revolt Desktop preload (window.__REVOLT_DESKTOP__).
     * @returns {boolean} Whether running in desktop app
     */
    isDesktopApp: () => {
      return typeof window !== 'undefined' && window.__REVOLT_DESKTOP__ === true;
    },

    /**
     * Get the Revolt Desktop build channel when running in the desktop app.
     * Set by preload (patched at build time: main, beta, or dev).
     * @returns {string|null} 'main' | 'beta' | 'dev' when in desktop app, null otherwise
     */
    getDesktopChannel: () => {
      return (typeof window !== 'undefined' && window.__REVOLT_DESKTOP_CHANNEL__) || null;
    },

    /**
     * Get the Revolt Desktop app version when running in the desktop app.
     * Set by preload (patched at build/start time: e.g. 0.1.2, 0.1.2-beta, 0.1.2-dev).
     * @returns {string|null} Version string when in desktop app, null otherwise
     */
    getDesktopVersion: () => {
      return (typeof window !== 'undefined' && window.__REVOLT_DESKTOP_VERSION__) || null;
    },

    /**
     * Check if running on a genuine mobile device (not just small screen)
     * @returns {boolean} Whether on mobile device
     */
    isMobileDevice: () => {
      return typeof navigator !== 'undefined' &&
             /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    },

    /**
     * Check if screen is mobile-sized (<= 768px)
     * @returns {boolean} Whether screen is mobile width
     */
    isMobileScreen: () => {
      return typeof window !== 'undefined' && window.innerWidth <= 768;
    },

    /**
     * Get comprehensive device/environment info
     * @returns {object} Device and environment information
     */
    getDeviceInfo: () => {
      const isDesktopApp = envUtils.device.isDesktopApp();
      const isMobileDevice = envUtils.device.isMobileDevice();
      const isMobileScreen = envUtils.device.isMobileScreen();

      return {
        // Environment type
        environment: isDesktopApp ? 'desktop' : 'web',

        // Device type detection
        device: {
          isMobile: isMobileDevice,
          isDesktop: !isMobileDevice,
          isTablet: false, // Could be enhanced later
          isTouch: 'ontouchstart' in window || navigator.maxTouchPoints > 0
        },

        // Screen information
        screen: {
          width: typeof window !== 'undefined' ? window.innerWidth : 0,
          height: typeof window !== 'undefined' ? window.innerHeight : 0,
          isMobileSize: isMobileScreen,
          pixelRatio: typeof window !== 'undefined' ? window.devicePixelRatio : 1
        },

        // Capabilities
        capabilities: {
          hasElectronAPI: isDesktopApp,
          hasLocalAI: isDesktopApp, // Desktop app can have local AI
          hasFileSystem: isDesktopApp, // Desktop app has file system access
          hasNativeMenus: isDesktopApp, // Desktop app has native window controls
          isEmbedded: isDesktopApp // Desktop app embeds web content
        },

        // Combined recommendations
        ui: {
          shouldUseMobileUI: isMobileDevice || (isMobileScreen && !isDesktopApp),
          shouldShowDesktopFeatures: isDesktopApp,
          shouldUseTouchOptimizations: isMobileDevice || ('ontouchstart' in window)
        }
      };
    },

    /**
     * Get definitive environment type (website, desktop, mobile)
     * @returns {string} 'website', 'desktop', or 'mobile'
     */
    getEnvironmentType: () => {
      // Check if running in Revolt Desktop Electron app
      if (envUtils.device.isDesktopApp()) {
        return 'desktop';
      }

      // Check if running on genuine mobile device
      if (envUtils.device.isMobileDevice()) {
        return 'mobile';
      }

      // Default to website (desktop browser)
      return 'website';
    },

    /**
     * Get recommended UI mode based on device and environment
     * @returns {string} 'mobile', 'desktop', or 'adaptive'
     */
    getRecommendedUIMode: () => {
      const info = envUtils.device.getDeviceInfo();

      if (info.device.isMobile) return 'mobile';
      if (info.environment === 'desktop') return 'desktop';
      if (info.screen.isMobileSize) return 'mobile';
      return 'desktop';
    }
  },

  /**
   * Get URL parameters safely
   * @param {string} param - Parameter name
   * @param {string} fallback - Fallback value
   * @returns {string} Parameter value or fallback
   */
  getUrlParam: (param, fallback = '') => {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      return urlParams.get(param) || fallback;
    } catch (error) {
      if (window.logger) {
        window.logger.warn({
          tags: "utils|env|url",
          color1: "yellow",
          includeSource: true
        }, `Failed to get URL parameter: ${param}`, error);
      }
      return fallback;
    }
  },

  /**
   * Build URL with query parameters for current section
   * Handles both development and production environments
   * @param {string} section - Section name (e.g., 'products')
   * @param {Object} params - Query parameters object
   * @returns {string} Complete URL with parameters
   */
  buildSectionUrl: (section, params = {}) => {
    try {
      const baseUrl = window.location.origin;
      const sectionPath = `/${section}`;

      // Filter out empty/undefined parameters
      const filteredParams = Object.entries(params).reduce((acc, [key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          acc[key] = value;
        }
        return acc;
      }, {});

      // Build query string
      const queryString = new URLSearchParams(filteredParams).toString();

      return `${baseUrl}${sectionPath}${queryString ? `?${queryString}` : ''}`;
    } catch (error) {
      if (window.logger) {
        window.logger.error({
          tags: "utils|env|url",
          color1: "red",
          includeSource: true
        }, `Failed to build section URL for ${section}:`, error);
      }
      return `/${section}`;
    }
  }
};

const USER_SETTINGS_METADATA = {
  developerMode: { defaultValue: false },
  easyAppManagement: { defaultValue: false, requiresDeveloperMode: true },
  detailedViewInfo: { defaultValue: false, requiresDeveloperMode: true },
  // Admin-only settings are filtered out by the backend for non-admin users.
  adminAppData: { defaultValue: false, requiresDeveloperMode: true, requiresAdmin: true },
};

const getDefaultUserSettings = () =>
  Object.fromEntries(
    Object.entries(USER_SETTINGS_METADATA).map(([key, metadata]) => [
      key,
      metadata.defaultValue,
    ])
  );

const evaluateUserSettings = (rawSettings = {}) => {
  const defaultSettings = getDefaultUserSettings();
  const developerModeEnabled = Boolean(rawSettings.developerMode);

  const evaluatedSettings = {
    ...defaultSettings,
    ...rawSettings,
    developerMode: developerModeEnabled,
  };

  for (const [key, metadata] of Object.entries(USER_SETTINGS_METADATA)) {
    if (!metadata.requiresDeveloperMode || key === "developerMode") {
      continue;
    }

    evaluatedSettings[key] =
      developerModeEnabled && Boolean(rawSettings[key]);
  }

  return evaluatedSettings;
};

/**
 * User settings utilities
 */
export const settingsUtils = {
  /**
   * Get evaluated user settings with business logic applied
   * @returns {Promise<Object>} Evaluated settings object
   */
  getEvaluatedSettings: async () => {
    try {
      const response = await fetch('/api/account/settings', {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch settings: ${response.status}`);
      }

      const data = await response.json();
      if (!data.success || !data.settings) {
        throw new Error('Invalid settings response');
      }

      return evaluateUserSettings(data.settings);
    } catch (error) {
      if (window.logger) {
        window.logger.error({
          tags: "utils|settings|evaluated",
          color1: "red",
          includeSource: true
        }, "Failed to get evaluated settings:", error);
      }
      // Return default evaluated settings on error
      return getDefaultUserSettings();
    }
  },

  /**
   * Get raw user settings (for configuration UIs)
   * @returns {Promise<Object>} Raw settings object
   */
  getRawSettings: async () => {
    try {
      const response = await fetch('/api/account/settings', {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch settings: ${response.status}`);
      }

      const data = await response.json();
      if (!data.success || !data.settings) {
        throw new Error('Invalid settings response');
      }

      return data.settings;
    } catch (error) {
      if (window.logger) {
        window.logger.error({
          tags: "utils|settings|raw",
          color1: "red",
          includeSource: true
        }, "Failed to get raw settings:", error);
      }
      // Return empty settings on error
      return {};
    }
  },

  /**
   * Update a user setting
   * @param {string} key - Setting key
   * @param {*} value - Setting value
   * @returns {Promise<boolean>} Success status
   */
  updateSetting: async (key, value) => {
    try {
      const response = await fetch(`/api/account/settings/${key}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ value }),
      });

      return response.ok;
    } catch (error) {
      if (window.logger) {
        window.logger.error({
          tags: "utils|settings|update",
          color1: "red",
          includeSource: true
        }, `Failed to update setting ${key}:`, error);
      }
      return false;
    }
  }
};

/**
 * Link processing utilities for Q&A links
 */
export const linkUtils = {
  /**
   * Process Q&A link URL based on type
   * @param {string} url - The link URL
   * @param {string} type - Link type ('direct', 'relative', or legacy 'internal'/'external')
   * @returns {string} Processed URL
   */
  processQALink: (url, type) => {
    if (!url) return '';

    if (type === 'direct' || type === 'internal' || type === 'external') {
      // Use URL as-is (absolute URLs) - includes legacy internal/external types
      return url;
    } else if (type === 'relative') {
      // Prepend current domain to make it absolute
      return `${window.location.origin}${url}`;
    }

    // Fallback: treat as direct
    return url;
  },

  /**
   * Check if a URL is absolute
   * @param {string} url - URL to check
   * @returns {boolean} True if absolute URL
   */
  isAbsoluteUrl: (url) => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  },

  /**
   * Process inline links in Q&A answer text
   * Syntax: [display text]{link name}
   * @param {string} text - The answer text to process
   * @param {Array} links - Array of link objects with title, url, type
   * @returns {Object} { processedText: string, usedLinkNames: Set }
   */
  processInlineLinks: (text, links) => {
    if (!text || !links || links.length === 0) {
      return { processedText: text, usedLinkNames: new Set() };
    }

    const usedLinkNames = new Set();
    let processedText = text;

    // Create a map of link names to link objects for quick lookup
    const linkMap = new Map();
    links.forEach(link => {
      if (link.title) {
        linkMap.set(link.title.toLowerCase(), link);
      }
    });

    // Regex to match [display text]{link name}
    const linkRegex = /\[([^\]]+)\]\{([^}]+)\}/g;

    processedText = processedText.replace(linkRegex, (match, displayText, linkName) => {
      const normalizedLinkName = linkName.toLowerCase().trim();
      const link = linkMap.get(normalizedLinkName);

      if (link) {
        // Found the link, create an anchor tag
        const url = linkUtils.processQALink(link.url, link.type);
        usedLinkNames.add(link.title);
        return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="inline-qa-link">${displayText}</a>`;
      } else {
        // Link not found, return the display text without link
        return displayText;
      }
    });

    return { processedText, usedLinkNames };
  },

  /**
   * Process error messages with inline links. Syntax: [display text]{url}
   * Used for backend error messages that may contain clickable links.
   * @param {string} text - The message text (e.g. from backend error)
   * @returns {string} HTML string with links rendered, non-link text escaped
   */
  processErrorMessageLinks: (text) => {
    if (!text || typeof text !== 'string') return '';

    const escapeHtml = (s) =>
      String(s)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');

    // Regex to match [display text]{url}
    const linkRegex = /\[([^\]]+)\]\{([^}]+)\}/g;
    let result = '';
    let lastIndex = 0;
    let match;

    while ((match = linkRegex.exec(text)) !== null) {
      result += escapeHtml(text.slice(lastIndex, match.index));
      const displayText = match[1];
      const url = String(match[2]).trim();
      if (/^https?:\/\//i.test(url)) {
        result += `<a href="${escapeHtml(url)}" target="_blank" rel="noopener noreferrer" class="ai-error-inline-link">${escapeHtml(displayText)}</a>`;
      } else {
        result += escapeHtml(displayText);
      }
      lastIndex = match.index + match[0].length;
    }
    result += escapeHtml(text.slice(lastIndex));
    return result;
  }
};
